import React, { useState, ReactNode, useMemo, useEffect, useRef } from "react";
import { Box, Card, CardContent, Chip, IconButton, Typography, Paper } from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import HandshakeIcon from "@mui/icons-material/Handshake";
import { jobOverlapsDay } from "./utils/jobOverlapsRange";
import { WeekJobs } from "./weekJobs";
import { Business, Close, Home, OpenInNew, WorkOutline } from "@mui/icons-material";
import { colors } from "../ui/colors";
import { useWidgetVariant } from "./useWidgetVariant";
import { RiveByKey } from "@components/riveAnimation";
import { JobDetModal } from "@components/jobs/jobs-list/job-card/JobDetModal";
import { BaseModalComponent } from "@components/common/BaseModalComponent";
import { JobLayout } from "@components/jobs/jobs-list/jobs-layout/Job.layout";

export type JobStatus = "support" | "scheduled" | "inProgress" | "completed" | "cancelled" | "approved" | "forRevision" | "Unknown";

export type JobType = "residential" | "commercial";

export type Step = {
  icon: ReactNode;
  label: string;
  value: JobStatus;
  time?: string;
};

export type CardProps = {
  id: string;
  title: string;
  status: string;
  description: string;
  progress: number;
  type: JobType;
  jobStatus: string;
  onOpen: (id: string) => void;
  onClose?: () => void;
  isSelected: boolean;
  alias?: string;
  jobId?: string | number;
  serviceOrderId?: string | number;
  client?: any;
  quote?: any;
  scopeOfWork?: any;
  jobTypeLabel?: string;
};

export type Job = {
  id: string; // id único del job
  userId: string; // id del usuario/técnico asignado
  serviceOrderId: string; // folio de la orden de servicio (ej: SO-1001)
  title: string; // nombre o título del trabajo
  description: string; // detalle breve de la tarea
  progress: number; // valor numérico de 0 a 100
  status: JobStatus; // estado operativo actual
  type: JobType; // categoría del cliente
  date: string; // fecha en formato ISO "YYYY-MM-DD" para el calendario
  hour: string; // hora en formato ISO "HH:mm" para el calendario
};

export const steps: Step[] = [
  {
    icon: <WorkOutline sx={{ fontSize: 30 }} />,
    label: "Scheduled",
    value: "scheduled",
  },
  {
    icon: <BuildIcon sx={{ fontSize: 30 }} />,
    label: "In Progress",
    value: "inProgress",
  },
  {
    icon: <HandshakeIcon sx={{ fontSize: 30 }} />,
    label: "Completed",
    value: "completed",
  },
];

export const getTypeColor = (type: JobType) => {
  return type === "residential" ? "#ff8912" : "#3b82f6";
};

const CompactInfoChip = ({ label, value }: { label: string; value?: string | null }) => {
  if (!value) return null;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.25,
        px: 0.65,
        py: 0.2,
        borderRadius: "999px",
        border: "1px solid var(--fx-surface-border, #E2E8F0)",
        bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
      }}
    >
      <Typography component="span" sx={{ fontSize: 9, color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 700, lineHeight: 1.15 }}>
        {label}:
      </Typography>
      <Typography
        component="span"
        noWrap
        sx={{ fontSize: 9, color: colors.gray[700], fontWeight: 600, lineHeight: 1.15, textTransform: "capitalize" }}
      >
        {value}
      </Typography>
    </Box>
  );
};

///////////////////////////////////////////////////////////////////

export const JobCard = ({
  id,
  title,
  description,
  progress,
  type,
  onOpen,
  isSelected,
  status,
  alias,
  jobId,
  serviceOrderId,
  client,
  quote,
  scopeOfWork,
  jobTypeLabel,
}: CardProps) => {
  const getStatusDetails = (stat: string) => {
    switch (stat?.toLowerCase()) {
      case "completed":
        return { label: "Completed", color: "#15803d", bg: "#f0fdf4" };
      case "inprogress":
        return { label: "In Progress", color: "#c2410c", bg: "#fff7ed" };
      case "scheduled":
        return { label: "Scheduled", color: "#6366f1", bg: "#eef2ff" };
      case "cancelled":
        return { label: "Cancelled", color: "#b91c1c", bg: "#fef2f2" };
      case "approved":
        return { label: "Approved", color: "#047857", bg: "#ecfdf5" };
      case "forrevision":
        return { label: "For Revision", color: "#92400e", bg: "#fffbeb" };
      case "support":
        return { label: "Support", color: "#0369a1", bg: "#f0f9ff" };
      default:
        return { label: "Unknown", color: "var(--fx-surface-textsecondary, #64748B)", bg: "#f3f4f6" };
    }
  };

  const mainColor = getTypeColor(type);
  const currentStatus = getStatusDetails(status);
  const { isLargeSlot } = useWidgetVariant(500);

  const displayJobId = serviceOrderId || jobId || id;
  const displayTitle =
    alias?.trim() ||
    quote?.alias?.trim() ||
    (!/^service\s*#/i.test(title || "") ? title : "") ||
    `Job #${displayJobId}`;
  const clientName =
    client?.firstName || client?.lastName
      ? [client?.firstName, client?.lastName].filter(Boolean).join(" ")
      : client?.alias || client?.businessName || client?.name || quote?.client?.alias || quote?.client?.businessName || "";
  const clientType = client?.type || quote?.client?.type || "";

  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        transition: "all 0.3s ease",
        backgroundColor: isSelected ? `${mainColor}08` : "var(--fx-surface-base, #FFFFFF)",
        boxShadow: isSelected ? "0 6px 18px rgba(15, 23, 42, 0.08)" : "none",
        mb: 0.5,
      }}
    >
      <CardContent
        onClick={() => onOpen(id)}
        sx={{
          py: 1,
          px: 1.5,
          cursor: "pointer",
          border: isSelected ? `1px solid ${mainColor}55` : "1px solid var(--fx-surface-border, #E2E8F0)",
          borderRadius: isSelected ? "0 6px 6px 0" : 1.5,
          transition: "background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
          "&:hover": {
            backgroundColor: `${mainColor}08`,
            borderColor: `${mainColor}55`,
            boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Box
              sx={{
                minWidth: 24,
                height: 24,
                borderRadius: 0.5,
                backgroundColor: `${mainColor}10`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: mainColor,
              }}
            >
              {type === "residential" ? <Home sx={{ fontSize: 15 }} /> : <Business sx={{ fontSize: 15 }} />}
            </Box>

            <Typography noWrap sx={{ fontSize: isLargeSlot ? 12 : 13, fontWeight: 600 }}>
              {displayTitle}
            </Typography>
          </Box>

          <Chip
            label={currentStatus.label}
            size="small"
            sx={{
              fontSize: 9,
              fontWeight: 700,
              height: 18,
              minWidth: 80,
              backgroundColor: currentStatus.bg,
              color: currentStatus.color,
              border: "none",
              textTransform: "uppercase",
            }}
          />
        </Box>

        <Box sx={{ ml: 4.25 }}>
          <Typography noWrap sx={{ fontSize: 11.5, color: "text.secondary", mb: 0.75 }}>
            {description}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mb: 0.75 }}>
            <CompactInfoChip label="Client" value={clientName} />
            <CompactInfoChip label="Client type" value={clientType} />
            <CompactInfoChip label="Job type" value={jobTypeLabel} />
          </Box>

          <Box sx={{ width: "100%", height: 6, backgroundColor: "var(--fx-surface-muted, #F1F5F9)", borderRadius: 1, overflow: "hidden" }}>
            <Box
              sx={{
                backgroundColor: mainColor,
                width: `${progress}%`,
                height: "100%",
                transition: "width 0.5s ease-in-out",
              }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const formatVisitDate = (value?: string | Date | null) => {
  if (!value) return "";

  const dateValue =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)
      ? new Date(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10)))
      : new Date(value);

  if (Number.isNaN(dateValue.getTime())) return "";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(dateValue);
};

export const CardDescription = ({
  job,
  nameJob,
  idJob,
  type,
  activeProgress,
  statusJob,
  onClose,
  orientation = "vertical",
}: {
  job: any;
  nameJob: string;
  idJob: string | number;
  statusJob: string;
  type: JobType;
  activeProgress: number;
  onClose: () => void;
  orientation?: "vertical" | "horizontal";
}) => {
  const isHorizontal = orientation === "horizontal";

  const STATUS_MAP: Record<string, number> = {
    scheduled: 0,
    support: 0,
    approved: 0,
    forrevision: 0,
    cancelled: 0,
    inprogress: 1,
    completed: 2,
  };

  const currentStepIndex = STATUS_MAP[statusJob?.toLowerCase()] ?? activeProgress;
  const jobId = job?.serviceOrderId || job?.jobId || job?.id || idJob;
  const eventUid = job?.eventUid || job?.event?.uid || job?.uid || job?.id;
  const visitDate = formatVisitDate(job?.date || job?.startDate);

  const jobForDetails = {
    ...job,
    id: jobId,
    alias: job?.alias || job?.title || nameJob,
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", p: 2, gap: 1.5, position: "relative" }}>
      <IconButton onClick={onClose} sx={{ position: "absolute", top: 8, right: 8, color: "text.secondary" }}>
        <Close fontSize="small" />
      </IconButton>

      <Box sx={{ pr: 4 }}>
        <BaseModalComponent
          modalLabel={`Job ${jobId}`}
          modalKey={`Dashboard-Visit-Stepper-${jobId}-${eventUid}`}
          elementTrigger={
            <Typography
              role="button"
              tabIndex={0}
              sx={{
                width: "fit-content",
                lineHeight: 1,
                fontSize: 14,
                fontWeight: 500,
                mb: 0.75,
                cursor: "pointer",
                color: colors.gray[600],
                textDecoration: "none",
                textUnderlineOffset: "2px",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
                transition: "color 0.18s ease, text-decoration-color 0.18s ease",
                "&:hover": {
                  color: colors.blue[600],
                  textDecoration: "underline",
                },
              }}
            >
              {visitDate ? `Visit ${visitDate}` : "Visit"}
              <OpenInNew sx={{ fontSize: 14, color: "currentColor", opacity: 0.75 }} />
            </Typography>
          }
        >
          <JobLayout job={jobForDetails} currentEventUid={eventUid} showCalendar />
        </BaseModalComponent>

        <JobDetModal
          job={jobForDetails}
          modalKey={`WeekWidget-JobCard-${jobId}`}
          wrapTrigger={false}
          trigger={
            <Typography
              role="button"
              tabIndex={0}
              sx={{
                width: "fit-content",
                textAlign: "start",
                lineHeight: 1,
                fontSize: 12,
                fontWeight: 500,
                mb: 0.5,
                cursor: "pointer",
                color: colors.gray[400],
                textDecoration: "none",
                textUnderlineOffset: "2px",
                transition: "color 0.18s ease, text-decoration-color 0.18s ease",
                "&:hover": {
                  color: colors.blue[600],
                  textDecoration: "underline",
                },
              }}
            >
              Job #{jobId}
            </Typography>
          }
        />

      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: isHorizontal ? "row" : "column",
          justifyContent: isHorizontal ? "space-between" : "flex-start",
          alignItems: isHorizontal ? "flex-start" : "stretch",
          gap: isHorizontal ? 0 : 1.5,
          height: isHorizontal ? 50 : "auto",
        }}
      >
        {steps.map((step, index) => {
          const isCompletedOrActive = index <= currentStepIndex;
          const isLastStep = index === steps.length - 1;
          const currentColor = isCompletedOrActive ? getTypeColor(type) : "#d1d5db";

          return (
            <React.Fragment key={step.value}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isHorizontal ? "column" : "row",
                  alignItems: "center",
                  gap: !isHorizontal ? 2 : 0,
                  textAlign: isHorizontal ? "center" : "left",
                  color: currentColor,
                  opacity: isCompletedOrActive ? 1 : 0.6,
                  flex: isHorizontal ? "1 1 0" : "none",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", "& svg": { fontSize: isHorizontal ? 24 : 30 } }}>{step.icon}</Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize: isHorizontal ? 11 : 14,
                      fontWeight: 600,
                      color: isCompletedOrActive ? "text.primary" : "text.secondary",
                    }}
                  >
                    {step.label}
                  </Typography>

                  {step.time && (
                    <Typography sx={{ fontSize: isHorizontal ? 10 : 12 }} color="text.secondary">
                      {step.time}
                    </Typography>
                  )}
                </Box>
              </Box>

              {isHorizontal && !isLastStep && (
                <Box
                  sx={{
                    flex: 1,
                    height: "2px",
                    bgcolor: index < currentStepIndex ? getTypeColor(type) : "var(--fx-surface-muted, #F1F5F9)",
                    mt: 1.6,
                    mx: 0.5,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

const JobsWidgetWithWeek = ({
  filter = "all",
  period = "weekly",
  jobs = [],
  isLoading = false,
  selectedDate: selectedDateProp = new Date(),
  lockWeek = false,
  timeZone,
}: {
  filter?: string;
  period?: string;
  jobs?: any[];
  isLoading?: boolean;
  selectedDate?: Date;
  lockWeek?: boolean;
  timeZone?: string;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date>(selectedDateProp);
  const [activeDay, setActiveDay] = useState<Date>(selectedDateProp);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const jobsReal = jobs;

  useEffect(() => {
    setSelectedDate(selectedDateProp);
  }, [selectedDateProp]);

  const filteredJobs = useMemo(() => {
    const currentFilter = filter.toLowerCase();

    const list = currentFilter === "all" ? [...jobsReal] : jobsReal.filter((job) => job?.type?.toLowerCase() === currentFilter);

    return list.filter((job) => {
      return jobOverlapsDay(job, activeDay, timeZone);
    });
  }, [filter, activeDay, jobsReal, timeZone]);

  useEffect(() => {
    if (selectedJobId && scrollContainerRef.current) {
      const timer = setTimeout(() => {
        scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [selectedJobId]);

  const handleOpen = (jobId: string) => {
    if (selectedJobId === jobId && open) {
      handleClose();
      return;
    }

    setSelectedJobId(jobId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedJobId("");
  };

  const selectedJob = useMemo(() => {
    if (!selectedJobId) return null;

    return jobsReal.find((item) => String(item.id) === String(selectedJobId));
  }, [selectedJobId, jobsReal]);

  const activeProgresspull = useMemo(() => {
    if (!selectedJob) return 0;

    const status = selectedJob.status?.toLowerCase();

    if (status === "completed") return 2;
    if (status === "inprogress") return 1;

    return 0;
  }, [selectedJob]);

  const { ref, isLargeSlot } = useWidgetVariant(500);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        maxHeight: isLargeSlot ? "100vh" : "350px",
        margin: "auto",
        backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
      }}
    >
      <Box sx={{ height: isLargeSlot ? "35%" : "200px", backgroundColor: "var(--fx-surface-base, #FFFFFF)" }}>
        <Box sx={{ height: isLargeSlot ? "100%" : "80%" }}>
          <WeekJobs
            selectedJob={filter}
            jobs={jobsReal}
            onDayClick={(day) => {
              setActiveDay(day);
              setSelectedJobId("");
              setOpen(false);
            }}
            activeDay={activeDay}
            referenceDate={selectedDateProp}
            timeZone={timeZone}
          />
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
          gap: 1,
          display: "flex",
          height: "65%",
          marginTop: isLargeSlot ? "0px" : "0px",
        }}
      >
        <Box
          ref={scrollContainerRef}
          sx={{
            pt: 1,
            flex: isLargeSlot ? (open ? 2 : 1) : open ? 0 : 1,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            overflowY: "auto",
            transition: "all 0.4s ease",
            minHeight: isLargeSlot ? "100%" : "24vh",
            colorScheme: "light",
            "&::-webkit-scrollbar": {
              width: "12px",
              display: "block",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(29, 28, 28, 0.5)",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "var(--fx-surface-muted, #F1F5F9)",
            },
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(42, 42, 42, 0.5) #f1f1f1",
          }}
        >
          <Typography
            sx={{
              color: colors.orange.base,
              fontWeight: 600,
              fontSize: "0.8rem",
              lineHeight: 1.2,
              my: 0.5,
            }}
          >
            {filteredJobs.length === 0 ? "No tasks scheduled" : `You have ${filteredJobs.length} ${filteredJobs.length === 1 ? "task" : "tasks"}`}
          </Typography>
          {filteredJobs.length > 0 ? (
            filteredJobs.map((job) => (
              <JobCard key={job.id} {...job} jobStatus={job.status} isSelected={selectedJobId === job.id} onOpen={handleOpen} />
            ))
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "60%",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "24px",
                  border: `1px solid rgba(255, 152, 0, 0.15)`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "auto",
                  px: 1.5,
                  pb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "hidden",
                  }}
                >
                  <RiveByKey riveKey="createjob" boxSize={200} />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    color: colors.orange.base,
                    fontWeight: 800,
                    fontSize: "1rem",
                    textAlign: "center",
                    marginTop: isLargeSlot ? -3 : -4,
                  }}
                >
                  Your day is wide open
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    textAlign: "center",
                    fontSize: "0.85rem",
                    opacity: 0.8,
                  }}
                >
                  There are no jobs scheduled for this day.
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flex: isLargeSlot ? (open ? 1 : 0) : open ? 1 : 0,
            opacity: open ? 1 : 0,
            overflowY: "auto",
            overflowX: "hidden",
            gap: 1,
            transition: "all 0.4s ease",
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            borderLeft: open ? "1px solid var(--fx-surface-border, #E2E8F0)" : "none",
            maxHeight: isLargeSlot ? "100vh" : "190px",
            mt: !isLargeSlot && 1,
          }}
        >
          {open && selectedJob && (
            <CardDescription
              job={selectedJob}
              nameJob={selectedJob.title}
              idJob={selectedJob.serviceOrderId || selectedJob.jobId || selectedJob.id}
              statusJob={selectedJob.status}
              type={selectedJob.type}
              activeProgress={activeProgresspull}
              onClose={handleClose}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default JobsWidgetWithWeek;
